import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { withStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Draggable from 'react-draggable';

import IconButton from 'components/common/IconButton';
import IconMoveVert from 'components/icons/IconMoveVert';

const styles = theme => ({
  root: {
    display: 'flex',
    position: 'relative',
    boxSizing: 'border-box',
    height: 44,
    '&:before': {
      content: '""',
      display: 'block',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: 1,
      zIndex: 1,
      backgroundColor: 'transparent',
    },
    '& + $root': {
      '&:before': {
        backgroundColor: theme.palette.primary.A100,
      },
    },
  },
  rootDragged: {
    zIndex: 20,
    '&:before': {
      zIndex: 0,
    },
  },
  rootHidden: {
    display: 'none',
  },
  draggable: {
    width: '100%',
  },
  content: {
    display: 'flex',
    alignItems: 'center',
    boxSizing: 'border-box',
    position: 'relative',
    height: 44,
    padding: '4px 16px 4px 42px',
    marginTop: 1,
  },
  contentDragged: {
    backgroundColor: theme.palette.grey[100],
  },
  row: {
    height: 24,
  },
  table: {
    width: 120,
    minWidth: 120,
  },
  cell: {
    border: 'none',
    background: 'none',
    padding: '4px 8px',
    '&:first-child': {
      padding: '4px 8px',
    },
    '&:last-child': {
      padding: '4px 8px',
    },
  },
  cellButton: {
    width: 0,
    padding: 0,
    '&:first-child': {
      padding: 0,
    },
  },
  cellDos: {
    width: 76,
    minWidth: 76,
  },
  cellDescription: {
    width: 300,
    minWidth: 300,
  },
  cellUnits: {
    width: 50,
  },
  buttonMove: {
    position: 'absolute',
    top: '50%',
    left: 0,
    marginTop: -24,
  },
  iconMoveVert: {
    width: 10,
    height: 16,
  },
  description: {
    marginBottom: 4,
    maxHeight: 45,
    overflow: 'hidden',
  },
});

class ScheduleCalendarItemOrderLine extends Component {
  constructor(props) {
    super(props);

    this.state = {
      dragged: false,
      position: null,
      bounds: null,
      width: 0,
      height: 0,
    };

    this.onDraggableDataChange = this.onDraggableDataChange.bind(this);
    this.onStartDrag = this.onStartDrag.bind(this);
    this.onDrag = this.onDrag.bind(this);
    this.onStopDrag = this.onStopDrag.bind(this);
  }

  componentDidMount() {
    this.onDraggableDataChange();
  }

  componentWillReceiveProps(nextProps) {
    if (!this.props.style.height && (nextProps.style.height !== undefined)) {
      this.onDraggableDataChange();
    }
  }

  onDraggableDataChange() {
    const { boundsSelector } = this.props;
    if (boundsSelector) {
      const draggableRefDOMRect = this.draggableRef.getBoundingClientRect();
      const boundsDOMRect = document.querySelector(boundsSelector).getBoundingClientRect();
      const left = boundsDOMRect.left - draggableRefDOMRect.left;
      const top = boundsDOMRect.top - draggableRefDOMRect.top;
      const right = boundsDOMRect.width - draggableRefDOMRect.width - left;
      const bottom = (boundsDOMRect.height - draggableRefDOMRect.height) + top + 20;
      this.setState({
        bounds: { left, top, right, bottom },
        width: draggableRefDOMRect.width,
        height: draggableRefDOMRect.height,
      });
    }
  }

  onStartDrag(event, data) {
    const { onStartDrag, ID } = this.props;
    const { bounds, width, height } = this.state;
    this.onDraggableDataChange();
    this.setState({ dragged: true });
    event.stopPropagation();
    if (onStartDrag) {
      onStartDrag({
        x: data.x - bounds.left,
        y: data.y - bounds.top,
        width,
        height,
        defaultX: -bounds.left,
        defaultY: -bounds.top,
        ID,
      });
    }
  }

  onDrag(event, data) {
    const { onDrag, ID } = this.props;
    const { bounds, width, height } = this.state;
    if (onDrag) {
      onDrag({
        x: data.x - bounds.left,
        y: data.y - bounds.top,
        width,
        height,
        defaultX: -bounds.left,
        defaultY: -bounds.top,
        ID,
      });
    }
  }

  onStopDrag(event, data) {
    const { onStopDrag, ID } = this.props;
    const { bounds, width, height } = this.state;
    this.setState({
      dragged: false,
      position: {
        x: 0,
        y: 0,
      },
    });
    if (onStopDrag) {
      onStopDrag({
        x: data.x - bounds.left,
        y: data.y - bounds.top,
        width,
        height,
        defaultX: -bounds.left,
        defaultY: -bounds.top,
        ID,
      });
    }
  }

  render() {
    const {
      className,
      hidden,
      disabled,
      classes,
      Units,
      Description,
      style,
      ...otherProps
    } = this.props;
    const { dragged, position, bounds } = this.state;
    return (
      <div
        className={classNames(classes.root, className, {
          [classes.rootDragged]: dragged,
          [classes.rootHidden]: hidden,
        })}
        role="presentation"
        onMouseDown={(event) => {
          event.stopPropagation();
        }}
        style={style}
      >
        <Draggable
          handle={`.${classes.buttonMove}`}
          {...bounds ? { bounds } : {}}
          {...position ? { position } : {}}
          onStart={this.onStartDrag}
          onDrag={this.onDrag}
          onStop={this.onStopDrag}
          disabled={disabled}
        >
          <div
            className={classes.draggable}
            ref={(draggableRef) => {
              this.draggableRef = draggableRef;
            }}
          >
            <div
              className={classNames(classes.content, {
                [classes.contentDragged]: dragged,
              })}
            >
              <Table className={classes.table}>
                <TableBody>
                  <TableRow className={classes.row}>
                    <TableCell className={classNames(classes.cell, classes.cellButton)}>
                      <IconButton
                        className={classes.buttonMove}
                        disabled={disabled}
                        {...!disabled && !dragged ? {
                          tooltipContent: 'Drag it to Change Delivery',
                        } : {}}
                      >
                        <IconMoveVert className={classes.iconMoveVert} />
                      </IconButton>
                    </TableCell>
                    <TableCell className={classNames(classes.cell, classes.cellDos)}>
                      {`${otherProps['Date of Service From']} - ${otherProps['Date of Service To']}`}
                    </TableCell>
                    <TableCell className={classNames(classes.cell, classes.cellDescription)}>
                      {Description}
                    </TableCell>
                    <TableCell className={classNames(classes.cell, classes.cellUnits)}>
                      {Units}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </div>
        </Draggable>
      </div>
    );
  }
}

ScheduleCalendarItemOrderLine.propTypes = {
  ID: PropTypes.number.isRequired,
  Units: PropTypes.number.isRequired,
  Description: PropTypes.string.isRequired,
  className: PropTypes.string,
  onStartDrag: PropTypes.func,
  onDrag: PropTypes.func,
  onStopDrag: PropTypes.func,
  boundsSelector: PropTypes.string,
  disabled: PropTypes.bool,
  hidden: PropTypes.bool,
  style: PropTypes.shape({
    height: PropTypes.string,
  }),
  classes: PropTypes.shape({
    root: PropTypes.string.isRequired,
    rootDragged: PropTypes.string.isRequired,
    rootHidden: PropTypes.string.isRequired,
    draggable: PropTypes.string.isRequired,
    content: PropTypes.string.isRequired,
    contentDragged: PropTypes.string.isRequired,
    row: PropTypes.string.isRequired,
    cell: PropTypes.string.isRequired,
    cellUnits: PropTypes.string.isRequired,
    buttonMove: PropTypes.string.isRequired,
    iconMoveVert: PropTypes.string.isRequired,
  }).isRequired,
};

ScheduleCalendarItemOrderLine.defaultProps = {
  hidden: false,
  disabled: false,
  className: '',
  onStartDrag: null,
  onDrag: null,
  onStopDrag: null,
  boundsSelector: null,
  style: {},
};

export default withStyles(styles)(ScheduleCalendarItemOrderLine);



// WEBPACK FOOTER //
// ./src/components/schedule/ScheduleCalendar/ScheduleCalendarItemOrderLine.js